import React, { useContext } from 'react';
import { Dialog, Box, IconButton, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import {
  createWindowedPaper,
  MinimizedContext,
  WindowedControlsContext,
} from '../../../components/Dialogs/WindowedPaper';
import OpportunityDetail from '../OpportunityDetail';

const WindowedPaper = createWindowedPaper({
  defaultSize: { width: 1120, height: 760 },
  minSize: { width: 520, height: 420 },
});

function MaximizeButton() {
  const { maximized, toggleMaximize } = useContext(WindowedControlsContext);
  return (
    <Tooltip title={maximized ? 'คืนขนาดเดิม' : 'ขยายเต็มจอ'}>
      <IconButton size="small" onClick={toggleMaximize} sx={{ color: '#fff' }}>
        {maximized ? (
          <FullscreenExitIcon fontSize="small" />
        ) : (
          <FullscreenIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

// popup "ลอย" เหนือหน้า แทนการ navigate ไปหน้าใหม่เต็มจอ — ย่อ/ขยายได้โดยไม่ unmount
// OpportunityDetail (ข้อมูลที่โหลดไว้แล้วไม่หายตอนขยายกลับมา เพราะ children ยังอยู่ใน DOM
// ตลอด แค่ซ่อนด้วย CSS display:none) ปิดจริง ๆ ถึงจะ unmount ปรับขนาดได้ทุกทิศทาง (ขอบ/มุม) +
// ขยายเต็มจอได้ — ดู components/Dialogs/WindowedPaper.js
// backdrop โปร่งใส (ไม่หรี่หน้าจอ) แต่ยังคลิกปิดได้เหมือน modal ทั่วไป (เดิม hideBackdrop ให้
// คลิกทะลุไปโต้ตอบกับตาราง P5 ด้านหลังได้ขณะ popup เปิดค้าง — ตัดพฤติกรรมนั้นทิ้งตามที่ user ขอ)
export function OpportunityPopup({
  id,
  minimized,
  onClose,
  onToggleMinimize,
  onPopOut,
}) {
  return (
    <MinimizedContext.Provider value={minimized}>
      <Dialog
        open={!!id}
        onClose={onClose}
        maxWidth={false}
        BackdropProps={{ sx: { backgroundColor: 'transparent' } }}
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        PaperProps={{
          sx: {
            m: 2,
            borderRadius: 3,
            boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.35)',
          },
        }}
        PaperComponent={WindowedPaper}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: { xs: 1.5, md: 2 },
            py: 1.25,
            background:
              'linear-gradient(135deg, rgb(var(--color-primary-800)) 0%, rgb(var(--color-primary-600)) 52%, rgb(var(--color-primary-450)) 100%)',
            color: '#fff',
            flexShrink: 0,
            borderBottom: '1px solid rgba(255,255,255,0.16)',
          }}
        >
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: 'rgba(255,255,255,0.16)',
              border: '1px solid rgba(255,255,255,0.18)',
            }}
          >
            <BusinessCenterOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.68)',
                lineHeight: 1.1,
                letterSpacing: 0.7,
              }}
            >
              MARKETING CRM
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              noWrap
              sx={{ lineHeight: 1.35 }}
            >
              โอกาสทางธุรกิจ
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              p: 0.25,
              borderRadius: 2,
              bgcolor: 'rgba(15,23,42,0.16)',
            }}
          >
            <Tooltip title={minimized ? 'ขยาย' : 'ย่อ'}>
              <IconButton
                size="small"
                onClick={onToggleMinimize}
                sx={{
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                }}
              >
                {minimized ? (
                  <OpenInFullIcon fontSize="small" />
                ) : (
                  <RemoveIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            {!minimized && (
              <>
                <MaximizeButton />
                <Tooltip title="แยกไปหน้าต่างใหม่">
                  <IconButton
                    size="small"
                    onClick={onPopOut}
                    sx={{
                      color: '#fff',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.16)' },
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="ปิด">
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: '#fff',
                  '&:hover': { bgcolor: 'rgba(248,113,113,0.28)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: minimized ? 'none' : 'block',
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? '#f8fafc' : 'background.default',
            p: { xs: 1.5, md: 3 },
          }}
        >
          {id && <OpportunityDetail propId={id} onClose={onClose} />}
        </Box>
      </Dialog>
    </MinimizedContext.Provider>
  );
}

export default OpportunityPopup;
